import type { Mutation } from '@familyhub/shared';
import { db } from '.';

/**
 * Making a refused change intelligible, and retrying the one kind that can be.
 *
 * A rejection the person cannot identify is worse than no message at all: they
 * learn a change was lost without learning which. So each one is described in
 * the terms they used — "Adding Havermelk to the list" — and offered the action
 * that actually applies to it.
 */

/** What the mutation was trying to do, in the household's own words. */
export async function describe(mutation: Mutation): Promise<string> {
  if (mutation.op === 'checkoff') {
    const item = await db.list_items.get(mutation.list_item_id);
    const name = item ? await nameOfGeneric(item.generic_product_id) : null;
    return name ? `${name} afvinken` : 'Iets van de lijst afvinken';
  }
  if (mutation.op === 'delete') {
    return 'Iets van de lijst halen';
  }
  switch (mutation.table) {
    case 'list_items': {
      const name = await nameOfGeneric(mutation.row.generic_product_id);
      return name ? `${name} op de lijst zetten` : 'Iets op de lijst zetten';
    }
    case 'generic_products':
      return `“${mutation.row.name}” aanmaken`;
    case 'store_category_order':
      return 'Je schapvolgorde opslaan';
    case 'suggestion_dismissals':
      return 'Een suggestie wegklikken';
    default:
      return 'Een wijziging op je lijst';
  }
}

async function nameOfGeneric(id: string): Promise<string | null> {
  return (await db.generic_products.get(id))?.name ?? null;
}

/**
 * Whether trying again could plausibly work.
 *
 * Only `missing_reference` qualifies. It means the change pointed at a row the
 * server had not seen — a product created on the other phone, still in flight —
 * and once that row lands the same change applies cleanly. Everything else is a
 * validation failure that will fail identically forever, and offering a retry
 * button for it would be a lie.
 */
export function isRetryable(code: string): boolean {
  return code === 'missing_reference';
}

/** Put it back in the outbox to go with the next sync. */
export async function retry(id: string): Promise<void> {
  const rejection = await db.rejections.get(id);
  if (!rejection || !isRetryable(rejection.code)) return;
  await db.transaction('rw', [db.rejections, db.outbox], async () => {
    await db.outbox.add({ mutation: rejection.mutation, queued_at: new Date().toISOString() });
    await db.rejections.delete(id);
  });
}

export async function discard(id: string): Promise<void> {
  await db.rejections.delete(id);
}
